import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, DollarSign, Loader2, Settings2 } from "lucide-react";
import { PortalShell } from "@/components/PortalShell";
import { MetricValue } from "@/components/marketing/MetricValue";
import { fetchCampaigns, fetchReport, money, num, roas } from "@/lib/marketing";

/** Marketing → Campaign Performance (index). */
export default function CampaignPerformance() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { campaigns } = await fetchCampaigns();
        const withData = await Promise.all(campaigns.map(async (c) => {
          const report = await fetchReport({
            campaign: c.reporting_campaign_id || undefined,
            campaign_key: c.campaign_id,
          });
          return { ...c, report };
        }));
        setRows(withData);
      } catch (e) {
        setError("Unable to load campaigns");
      }
    })();
  }, []);

  return (
    <PortalShell title="Campaign Performance"
      subtitle="Marketing · attribution, funnel and revenue by campaign">
      <div className="flex flex-wrap gap-2 mb-5">
        <Link to="/admin/marketing/campaign-settings" data-testid="index-link-settings"
          className="inline-flex items-center gap-2 rounded-full border border-slate-300
            px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50">
          <Settings2 className="h-4 w-4" /> Campaign Settings
        </Link>
        <Link to="/admin/marketing/ad-spend" data-testid="index-link-ad-spend"
          className="inline-flex items-center gap-2 rounded-full border border-slate-300
            px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50">
          <DollarSign className="h-4 w-4" /> Manage Ad Spend
        </Link>
      </div>

      {error && <p className="text-sm text-rose-600">{error}</p>}
      {!rows && !error && (
        <p className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading campaigns…
        </p>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {(rows || []).map((c) => (
          <Link key={c.campaign_id} data-testid={`campaign-card-${c.campaign_id}`}
            to={`/admin/marketing/campaign-performance/${c.campaign_id}`}
            className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm
              hover:shadow-md hover:-translate-y-0.5 transition-all duration-200">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-black text-slate-900">{c.campaign_name}</h2>
                <p className="text-xs text-slate-500 mt-0.5">{c.platform}</p>
              </div>
              <span data-testid={`campaign-status-${c.campaign_id}`}
                className={`rounded-full px-2.5 py-1 text-[11px] font-black border ${
                  c.active ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : "bg-slate-100 text-slate-500 border-slate-200"}`}>
                {c.active ? "ACTIVE" : "INACTIVE"}
              </span>
            </div>

            <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
              {[["Spend", { value: c.report.spend.campaign_spend || null,
                            reason: "no_ad_spend" }, money],
                ["Bookings", { value: c.report.volumes.health_check_bookings }, num],
                ["Combined net revenue",
                 { value: c.report.volumes.combined_net_revenue }, money],
                ["Combined ROAS", c.report.kpis.combined_roas, roas],
              ].map(([label, metric, format]) => (
                <div key={label}>
                  <dt className="text-[11px] uppercase tracking-wider font-bold text-slate-500">
                    {label}
                  </dt>
                  <dd className="text-base font-black text-slate-900">
                    <MetricValue metric={metric} format={format}
                      testid={`card-${c.campaign_id}-${label.toLowerCase().replace(/\s+/g, "-")}`} />
                  </dd>
                </div>
              ))}
            </dl>

            <div className="flex items-center justify-between mt-4 text-[11px]">
              <span data-testid={`campaign-quality-${c.campaign_id}`}
                className={`rounded-full px-2 py-0.5 font-bold border ${
                  c.report.data_quality.overall === "healthy"
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : c.report.data_quality.overall === "attention"
                      ? "bg-amber-50 text-amber-700 border-amber-200"
                      : "bg-slate-100 text-slate-600 border-slate-200"}`}>
                Data: {c.report.data_quality.overall}
              </span>
              <span className="text-slate-500">
                Last activity: {c.last_activity ? c.last_activity.slice(0, 10) : "—"}
              </span>
              <span className="inline-flex items-center gap-1 font-bold text-slate-700">
                View <ArrowRight className="h-3 w-3" />
              </span>
            </div>
          </Link>
        ))}
      </div>

      {rows && !rows.length && (
        <p data-testid="campaign-index-empty" className="text-sm text-slate-500">
          No campaigns defined yet.
        </p>
      )}
    </PortalShell>
  );
}
